import { lazy } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { DropdownItem } from 'src/componentes/dropdown';
import Tooltip from 'src/componentes/tooltip';

import ContentManager from 'src/componentes/content-manager';
import { capitalizeFirstLetter } from 'src/utils/string';
import { STATUS_ITEM } from 'src/utils/constants';

import history from 'src/history';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';

import { SophieIcon } from 'src/componentes/icons';
import LucideIcon from 'src/componentes/lucide-icon';
import useAuth from 'src/hooks/useAuth';

const Mensagem = lazy(() => import('src/paginas/mensagem/modal'));
const EmbedPowerBI = lazy(() =>
  import('src/paginas/power-bi/componentes/power-bi-dialog')
);
const RiscoEditView = lazy(() =>
  import('src/paginas/gestao-risco/risco/RiscoEditView')
);
const ModalAvaliacoesRisco = lazy(() =>
  import('src/paginas/gestao-risco/risco/Avaliacao/ModalAvaliacoesRisco')
);

const DropdownItemRisco = (props) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();

  const {
    item,
    exibirRelacionamento: exibirRelacionamentoProp = true,
    exibirMensagem: exibirMensagemProp = true,
    exibirInteligenciaArtificial = false,
    showEdit = false,
    labelAI = '',
    tooltipAI,
    exibirPowerBI: exibirPowerBIProp = true,
    exibirPlanoAcao: exibirPlanoAcaoProp = true,
    exibirAvaliacoes: exibirAvaliacoesProp = true,
    handleEditClose,
    callback,
    handleCloseModal,
    handleClickSophieCallBack,
    filtro,
  } = props;

  const obterFiltroUrl = (itemAtual) => {
    if (!itemAtual) return;

    let url = `?itemId=${itemAtual.id}`;

    if (filtro != null && filtro != undefined) {
      url += `&openLastFilter=false`;

      // Tipo Filtro Data
      if (filtro.tipoFiltroData != null && filtro.tipoFiltroData.id > 0)
        url += `&tipoFiltroDataId=${filtro.tipoFiltroData.id}`;

      // Data Inicio
      if (filtro.dataInicio != null) url += `&dataInicio=${filtro.dataInicio}`;

      // Data Termino
      if (filtro.dataTermino != null)
        url += `&dataTermino=${filtro.dataTermino}`;
    }

    return url;
  };

  const handleEditClick = (itemAtual) => {
    if (!itemAtual) return;

    ContentManager.addContent(
      <RiscoEditView mode="edit" handleClose={handleEditClose} {...props} />
    );
  };

  const handleAvaliacoesClick = (itemAtual) => {
    if (!itemAtual) return;

    ContentManager.addContent(<ModalAvaliacoesRisco item={itemAtual} />);
  };

  const handlePlanoAcaoClick = (itemAtual) => {
    let url = obterFiltroUrl(itemAtual);

    history.push({
      pathname: '/gerenciar/gantt',
      search: url,
    });

    callback && callback();
    handleCloseModal && handleCloseModal();
  };

  const handleMensagemClick = (itemAtual) => {
    ContentManager.addContent(
      <Mensagem fromItem item={{ id: itemAtual.id }} />
    );
    callback && callback();
  };

  const handleRelacionamentosClick = (itemAtual) => {
    let url = obterFiltroUrl(itemAtual);

    history.push({
      pathname: '/gerenciar/arvore',
      search: url,
    });

    callback && callback();
    handleCloseModal && handleCloseModal();
  };

  const handleClickSophie = (itemAtual) => {
    handleClickSophieCallBack && handleClickSophieCallBack(itemAtual);
  };

  const handleRelatorioBIClick = (itemAtual) => {
    http
      .post(`/PowerBI/ObterConfiguracoesPorTipoItem`, {
        itemId: itemAtual.id,
        tipoItemId: itemAtual.tipo?.id,
      })
      .then((response) => {
        if (itemAtual.desdobramento) {
          response.data.model.campo = null;
        }
        ContentManager.addContent(
          <EmbedPowerBI
            itemId={itemAtual.id}
            relatorioPBI={response.data.model.relatorioPBI}
            embedURL={response.data.model.embedURL}
            workspacePBI={response.data.model.workspacePBI}
            filtros={response.data.model.filtros}
            embedToken={response.data.model.embedToken}
            usarAutenticacaoMicrosoft={
              response.data.model.usarAutenticacaoMicrosoft
            }
          />
        );
      })
      .catch((error) => {
        errorHandler(error);
      });

    callback && callback();
  };

  const tipoId = item && item.tipo && item.tipo.id;

  const reprovadoDefinitivoFluxoAprovacao =
    item?.fluxoAprovacao && item?.status?.id === STATUS_ITEM.REPROVADO;
  const reprovadoFluxoAprovacao =
    item?.fluxoAprovacao &&
    item?.status?.id === STATUS_ITEM.REPROVADO_AGUARDANDO_SUBMISSAO;
  const emPlanejamentoFluxoAprovacao =
    item?.fluxoAprovacao && item?.status?.id === STATUS_ITEM.EM_PLANEJAMENTO;

  const exibirPowerBI = exibirPowerBIProp
    ? item?.permissions
      ? item?.permissions?.allowViewPowerBI
      : true
    : false;

  if (item && item.permissions && item.permissions.allowBasic) return null;

  let exibirRelacionamento = exibirRelacionamentoProp;
  let exibirPlanoAcao = exibirPlanoAcaoProp;
  let exibirMensagem = exibirMensagemProp;
  let exibirAvaliacoes = exibirAvaliacoesProp;

  if (tipoId == 6) {
    exibirRelacionamento = true;
    exibirAvaliacoes = true;
  }

  if (item && item.ocultarMenus) {
    exibirRelacionamento = false;
    exibirPlanoAcao = false;
    exibirMensagem = false;
    exibirAvaliacoes = false;
  }

  if (item && item.exibirItensCardMetas) {
    exibirRelacionamento = false;
    exibirPlanoAcao = false;
    exibirMensagem = false;
    exibirAvaliacoes = false;
  }

  return [
    showEdit && (
      <DropdownItem key="1" onClick={() => handleEditClick(item)}>
        <LucideIcon icon="Pencil" />
        <FormattedMessage
          id="label.editar"
          values={{ editar: resources.editar }}
        />
      </DropdownItem>
    ),

    !reprovadoDefinitivoFluxoAprovacao &&
      !reprovadoFluxoAprovacao &&
      !emPlanejamentoFluxoAprovacao &&
      exibirAvaliacoes && (
        <DropdownItem key="3" onClick={() => handleAvaliacoesClick(item)}>
          <LucideIcon icon="ClipboardList" />
          {capitalizeFirstLetter(resources.avaliacoes)}
        </DropdownItem>
      ),

    exibirRelacionamento && (
      <DropdownItem key="4" onClick={() => handleRelacionamentosClick(item)}>
        <LucideIcon icon="Network" />
        {capitalizeFirstLetter(resources.relacionamentos)}
      </DropdownItem>
    ),

    !reprovadoDefinitivoFluxoAprovacao &&
      !reprovadoFluxoAprovacao &&
      !emPlanejamentoFluxoAprovacao &&
      exibirPlanoAcao &&
      tipoId !== 2 && (
        <DropdownItem key="6" onClick={() => handlePlanoAcaoClick(item)}>
          <LucideIcon icon="ChartNoAxesGantt" />
          {capitalizeFirstLetter(resources.planodeacao)}
        </DropdownItem>
      ),

    exibirMensagem && (
      <DropdownItem key="10" onClick={() => handleMensagemClick(item)}>
        <LucideIcon icon="MessageSquareText" />
        <FormattedMessage id="label.mensagens" />
      </DropdownItem>
    ),

    exibirPowerBI && (
      <DropdownItem key="13" onClick={() => handleRelatorioBIClick(item)}>
        <LucideIcon icon="Clipboard" />
        <FormattedMessage id="bi" />
      </DropdownItem>
    ),

    exibirInteligenciaArtificial && (
      <Tooltip
        key="16"
        text={
          tooltipAI
            ? intl.formatMessage(
                { id: tooltipAI },
                { riscos: resources.riscos }
              )
            : undefined
        }
      >
        <DropdownItem onClick={() => handleClickSophie(item)}>
          <SophieIcon />
          {intl.formatMessage({ id: labelAI }, { riscos: resources.riscos })}
        </DropdownItem>
      </Tooltip>
    ),
  ];
};

export default DropdownItemRisco;
